import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { getDeletionStatus, getMe, logout as apiLogout, refreshSession } from '../api/account';
import { ApiError, setUnauthorizedHandler } from '../api/client';
import { AuthContext, type AuthState } from './AuthContext';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
  });

  const loadUser = useCallback(async (): Promise<boolean> => {
    try {
      const user = await getMe().catch(() => getDeletionStatus());
      setState({ user, loading: false });
      return true;
    } catch {
      setState({ user: null, loading: false });
      return false;
    }
  }, []);

  const markAuthenticated = useCallback(async () => {
    await loadUser();
  }, [loadUser]);

  const refreshUser = useCallback(async () => {
    await loadUser();
  }, [loadUser]);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      // Even if the network call fails, drop the local cached user so
      // the UI returns to a signed-out state.
    }
    setState({ user: null, loading: false });
  }, []);

  // Register a 401-recovery hook. When any API call returns 401, the
  // client asks this function to refresh the session. If that succeeds,
  // the original request is retried transparently; if not, we fall back
  // to the signed-out state.
  useEffect(() => {
    setUnauthorizedHandler(async () => {
      try {
        await refreshSession();
        return true;
      } catch {
        setState({ user: null, loading: false });
        return false;
      }
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  // Bootstrap: try to hydrate the user from the session cookie. If that
  // 401s, try a refresh once and retry.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const user = await getMe().catch(() => getDeletionStatus());
        if (!cancelled) setState({ user, loading: false });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          try {
            await refreshSession();
            const user = await getMe().catch(() => getDeletionStatus());
            if (!cancelled) setState({ user, loading: false });
            return;
          } catch {
            // Fall through to signed-out state below.
          }
        }
        if (!cancelled) setState({ user: null, loading: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, refreshUser, markAuthenticated, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
